import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Volume2, Square, Loader2, Upload, Trash2, Plus } from "lucide-react";
import { api } from "../api";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const GOLD_TOAST = { background: "#1A1A1A", color: "#D4AF37", border: "1px solid rgba(212,175,55,0.4)" };
const LANGS = [
  { code: "en", label: "ENGLISH" },
  // hi/pa paused — restore when a better TTS replaces ElevenLabs Indic voices
];
const PUNJABI_TOOLS = true; // Sarvam AI audition live — Punjabi plays to guests only after APPROVE

const PreviewBtn = ({ slot, text }) => {
  const [state, setState] = useState("idle");
  const play = async () => {
    if (state === "playing") {
      window.__xpPrev?.pause();
      window.__xpPrev = null;
      setState("idle");
      return;
    }
    if (!text.trim()) return;
    setState("loading");
    try {
      const r = await api.post("/admin/voice-preview", { slot, text });
      if (!r.data.audio_url) return setState("idle");
      window.__xpPrev?.pause();
      const a = new Audio(`${BACKEND_URL}${r.data.audio_url}`);
      window.__xpPrev = a;
      a.onended = () => setState("idle");
      await a.play();
      setState("playing");
    } catch {
      setState("idle");
    }
  };
  return (
    <button
      data-testid={`preview-${slot.replace(/\|/g, "-")}`}
      onClick={play}
      aria-label="Preview in VeerJee's voice"
      className={`flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[9px] font-bold tracking-widest border ${
        state === "playing" ? "bg-[#D4AF37] text-black border-[#D4AF37]" : "border-[#D4AF37]/50 text-[#D4AF37]"
      }`}
    >
      {state === "loading" ? <Loader2 size={12} className="animate-spin" /> : state === "playing" ? <Square size={10} /> : <Volume2 size={12} />}
      {state === "playing" ? "STOP" : "HEAR IT"}
    </button>
  );
};

const Area = ({ value, onChange, testId, highlight }) => (
  <textarea
    data-testid={testId}
    value={value}
    onChange={(e) => onChange(e.target.value)}
    rows={2}
    className={`w-full bg-[#1A1A1A] border rounded-xl px-3 py-2.5 text-xs leading-relaxed focus:outline-none focus:border-[#D4AF37]/60 ${highlight ? "border-[#D4AF37]/60" : "border-white/10"}`}
  />
);

const MASCOT_KINDS = [
  { kind: "dish-added", label: "DISH ADDED TO ORDER" },
  { kind: "reward", label: "REWARD UNLOCKED" },
  { kind: "quiz-correct", label: "QUIZ ANSWERED RIGHT" },
];

const DELIVERIES = [
  { id: "clear", name: "CALM & CLEAR", desc: "Slower and steadier — easiest to understand (recommended)" },
  { id: "warm", name: "WARM HOST", desc: "A touch slower with more charm and expression" },
  { id: "original", name: "AS RECORDED", desc: "The original pace of the cloned recording" },
];

export const AdminVoice = () => {
  const [data, setData] = useState(null);
  const [lang, setLang] = useState("en");
  const [saving, setSaving] = useState(false);
  const [voices, setVoices] = useState(null);
  const [settings, setSettings] = useState(null);
  const [sample, setSample] = useState(null);
  const [narrators, setNarrators] = useState(null);
  const [narrSample, setNarrSample] = useState(null);
  const [punjabi, setPunjabi] = useState(null);
  const [pjSample, setPjSample] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [pron, setPron] = useState(null);
  const [pronPlaying, setPronPlaying] = useState(null);
  const [newTerm, setNewTerm] = useState("");
  const [newSpoken, setNewSpoken] = useState("");
  const [purging, setPurging] = useState(false);
  const sampleAudio = useRef(null);

  useEffect(() => {
    api.get("/admin/voice-scripts").then((r) => setData(r.data));
    api.get("/admin/voices").then((r) => setVoices(r.data)).catch(() => {});
    api.get("/admin/settings").then((r) => setSettings(r.data)).catch(() => {});
    api.get("/admin/narrators").then((r) => setNarrators(r.data)).catch(() => {});
    api.get("/admin/punjabi-greeting").then((r) => setPunjabi(r.data)).catch(() => {});
    api.get("/admin/pronunciations").then((r) => setPron(r.data)).catch(() => {});
  }, []);

  const playPjSample = async (id) => {
    if (pjSample === `playing:${id}`) {
      sampleAudio.current?.pause();
      setPjSample(null);
      return;
    }
    sampleAudio.current?.pause();
    setPjSample(id);
    try {
      let url;
      if (id === "upload") {
        url = `${BACKEND_URL}/api/experience/punjabi-upload-audio`;
      } else {
        const r = await api.post("/admin/punjabi-sample", { candidate_id: id });
        if (r.data.waiting_key) {
          setPjSample(null);
          toast("Waiting for your Sarvam AI key — add SARVAM_API_KEY in the Secrets panel", { style: GOLD_TOAST });
          return;
        }
        if (!r.data.audio_url) return setPjSample(null);
        url = `${BACKEND_URL}${r.data.audio_url}`;
      }
      const a = new Audio(url);
      sampleAudio.current = a;
      a.onended = () => setPjSample(null);
      await a.play();
      setPjSample(`playing:${id}`);
    } catch {
      setPjSample(null);
      toast.error("Could not play the Punjabi sample");
    }
  };

  const approvePunjabi = async (source) => {
    try {
      const r = await api.put("/admin/punjabi-voice", { source });
      setPunjabi(r.data);
      toast.success(source ? "Punjabi greeting approved — guests hear it before the English welcome" : "Punjabi greeting turned off", { style: GOLD_TOAST });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save the Punjabi greeting");
    }
  };

  const uploadPunjabi = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post("/admin/punjabi-upload", fd);
      const r = await api.get("/admin/punjabi-greeting");
      setPunjabi(r.data);
      toast.success("Recording uploaded — tap HEAR IT, then APPROVE to publish it", { style: GOLD_TOAST });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not upload the recording");
    }
    setUploading(false);
  };

  const playPron = async (term, spoken) => {
    if (pronPlaying === `playing:${term}`) {
      sampleAudio.current?.pause();
      setPronPlaying(null);
      return;
    }
    if (!term.trim() || !spoken.trim()) return;
    sampleAudio.current?.pause();
    setPronPlaying(term);
    try {
      const r = await api.post("/admin/pron-sample", { term, spoken });
      if (!r.data.audio_url) return setPronPlaying(null);
      const a = new Audio(`${BACKEND_URL}${r.data.audio_url}`);
      sampleAudio.current = a;
      a.onended = () => setPronPlaying(null);
      await a.play();
      setPronPlaying(`playing:${term}`);
    } catch (e) {
      setPronPlaying(null);
      toast.error(e.response?.data?.detail || "Could not play the sample");
    }
  };

  const savePron = async (words) => {
    try {
      const r = await api.put("/admin/pronunciations", { words });
      setPron(r.data);
      toast.success("Pronunciations saved — affected lines re-record on their next play", { style: GOLD_TOAST });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save pronunciations");
    }
  };

  const purgeAudio = async () => {
    if (!window.confirm("Delete every cached audio clip? Each line re-records once in the approved voice on its next play (one-time cost).")) return;
    setPurging(true);
    try {
      const r = await api.post("/admin/purge-audio");
      toast.success(`Cleared ${r.data.cleared} old clips — all audio now re-records fresh`, { style: GOLD_TOAST });
    } catch {
      toast.error("Could not clear the audio cache");
    }
    setPurging(false);
  };

  const playNarrSample = async (id) => {
    if (narrSample === `playing:${id}`) {
      sampleAudio.current?.pause();
      setNarrSample(null);
      return;
    }
    sampleAudio.current?.pause();
    setNarrSample(id);
    try {
      const r = await api.post("/admin/narrator-sample", { narrator_id: id });
      if (!r.data.audio_url) return setNarrSample(null);
      const a = new Audio(`${BACKEND_URL}${r.data.audio_url}`);
      sampleAudio.current = a;
      a.onended = () => setNarrSample(null);
      await a.play();
      setNarrSample(`playing:${id}`);
    } catch {
      setNarrSample(null);
      toast.error("Could not play the voice sample");
    }
  };

  const pickNarrator = async (id) => {
    try {
      const r = await api.put("/admin/narrator", { narrator_id: id });
      setNarrators({ ...narrators, active: r.data.active, narrators: narrators.narrators.map((n) => ({ ...n, active: n.voice_id === r.data.active })) });
      api.get("/admin/voices").then((rv) => setVoices(rv.data)).catch(() => {});
      toast.success("Narrator updated — every line re-records once in the new voice on its next play", { style: GOLD_TOAST });
    } catch {
      toast.error("Could not change the narrator");
    }
  };

  const saveNarration = async (patch) => {
    try {
      const r = await api.put("/admin/settings", patch);
      setSettings(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save narration settings");
    }
  };

  const pickDelivery = async (d) => {
    try {
      const r = await api.put("/admin/settings", { voice_delivery: d });
      setSettings(r.data);
      toast.success("Delivery updated — each line re-records once at the new pace on its next play", { style: GOLD_TOAST });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not change the delivery");
    }
  };

  const playSample = async (id) => {
    if (sample === `playing:${id}`) {
      sampleAudio.current?.pause();
      setSample(null);
      return;
    }
    sampleAudio.current?.pause();
    setSample(id);
    try {
      const r = await api.post("/admin/delivery-sample", { delivery: id });
      if (!r.data.audio_url) return setSample(null);
      const a = new Audio(`${BACKEND_URL}${r.data.audio_url}`);
      sampleAudio.current = a;
      a.onended = () => setSample(null);
      await a.play();
      setSample(`playing:${id}`);
    } catch {
      setSample(null);
      toast.error("Could not play the sample");
    }
  };

  const pickVoice = async (vid) => {
    try {
      const r = await api.put("/admin/voice", { voice_id: vid === voices.default ? "" : vid });
      setVoices({ ...voices, active: r.data.active, voices: voices.voices.map((v) => ({ ...v, active: v.voice_id === r.data.active })) });
      toast.success("Voice updated — every line re-records once in the new voice on its next play", { style: GOLD_TOAST });
    } catch {
      toast.error("Could not change the voice");
    }
  };

  if (!data) return <p className="text-xs text-white/40">Loading voice dialog…</p>;

  const setGreeting = (i, v) =>
    setData({ ...data, greetings: { ...data.greetings, [lang]: data.greetings[lang].map((s, x) => (x === i ? v : s)) } });
  const setAttract = (i, v) => setData({ ...data, attract: data.attract.map((s, x) => (x === i ? v : s)) });
  const setMascotLine = (kind, v) =>
    setData({ ...data, mascot: { ...data.mascot, [kind]: { ...data.mascot[kind], [lang]: v } } });

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put("/admin/voice-scripts", { greetings: data.greetings, attract: data.attract, sting: data.sting, mascot: data.mascot });
      setData(r.data);
      toast.success("Voice dialog saved — edited lines re-record in VeerJee's voice on their next play", { style: GOLD_TOAST });
    } catch {
      toast.error("Could not save — check that no line is missing");
    }
    setSaving(false);
  };

  return (
    <div data-testid="admin-voice" className="space-y-5">
      <div className="rounded-xl bg-[#121212] border border-white/10 p-4">
        <p className="text-[10px] tracking-widest text-[#D4AF37] mb-1">WHAT VEERGI SAYS</p>
        <p className="text-xs text-white/50">
          These are the exact lines guests hear, spoken in the cloned VeerJee voice. One line per weekday — today's line is outlined in gold.
          Tap HEAR IT to preview any line before guests do. Edited lines re-record automatically (small one-time cost per changed line).
          Write "VeerGi" — it is pronounced "VeerJee" automatically.
        </p>
      </div>

      {PUNJABI_TOOLS && punjabi && (
        <div className="rounded-xl bg-[#121212] border border-[#D4AF37]/40 p-4" data-testid="admin-punjabi-greeting">
          <p className="text-[10px] tracking-widest text-[#D4AF37] mb-1">PUNJABI WELCOME — "{punjabi.romanized}"</p>
          <p className="font-display text-lg text-white/90 mb-1">{punjabi.phrase}</p>
          <p className="text-xs text-white/50 mb-3">
            Guests hear this authentic Punjabi phrase first, then the English welcome in your chosen narrator voice.
            Nothing plays to guests until you APPROVE a version below. If no AI sample sounds right, upload a human recording.
          </p>
          {!punjabi.sarvam_ready && (
            <p data-testid="punjabi-sarvam-waiting" className="text-[10px] leading-relaxed text-[#E2A158] border border-[#E2A158]/40 bg-[#E2A158]/10 rounded-xl px-3 py-2 mb-3">
              NEW: Sarvam AI native Punjabi voices are ready to audition — they just need a key.
              Create one at dashboard.sarvam.ai, add it as SARVAM_API_KEY in your project's Secrets panel (never paste it in chat), then tap HEAR IT.
            </p>
          )}
          <div className="space-y-2">
            {punjabi.candidates.map((c) => (
              <div key={c.id} data-testid={`punjabi-${c.id}`}
                className={`rounded-xl border px-4 py-3 ${c.active ? "border-[#D4AF37] bg-[#D4AF37]/10" : "border-white/10 bg-[#1A1A1A]"}`}>
                <div className="flex items-center justify-between gap-2">
                  <span className="text-xs font-bold min-w-0 flex-1">
                    <span className="block leading-snug">{c.name}</span>
                    {c.provider === "sarvam" && (
                      <span data-testid={`punjabi-sarvam-chip-${c.id}`} className="inline-block mt-1 whitespace-nowrap text-[8px] font-black tracking-widest text-black bg-[#6FBF73] rounded-full px-2 py-0.5">
                        NEW · SARVAM AI
                      </span>
                    )}
                  </span>
                  <span className="flex items-center gap-1.5 shrink-0">
                    <button data-testid={`punjabi-sample-${c.id}`} onClick={() => playPjSample(c.id)}
                      className="flex items-center gap-1 text-[9px] font-bold tracking-widest text-[#D4AF37] border border-[#D4AF37]/50 rounded-full px-2.5 py-1">
                      {pjSample === c.id ? <Loader2 size={10} className="animate-spin" /> : pjSample === `playing:${c.id}` ? <Square size={9} /> : <Volume2 size={10} />}
                      {pjSample === `playing:${c.id}` ? "STOP" : "HEAR IT"}
                    </button>
                    {c.active ? (
                      <span data-testid={`punjabi-active-${c.id}`} className="text-[9px] font-black tracking-widest text-black bg-[#D4AF37] rounded-full px-3 py-1">APPROVED</span>
                    ) : (
                      <button data-testid={`punjabi-approve-${c.id}`} onClick={() => approvePunjabi(c.id)}
                        className="text-[9px] font-bold tracking-widest text-white/70 border border-white/25 rounded-full px-3 py-1">APPROVE</button>
                    )}
                  </span>
                </div>
                <span className="block text-[10px] text-white/50 mt-0.5">{c.desc}</span>
              </div>
            ))}
            <div className={`rounded-xl border px-4 py-3 ${punjabi.upload_active ? "border-[#D4AF37] bg-[#D4AF37]/10" : "border-white/10 bg-[#1A1A1A]"}`} data-testid="punjabi-upload-row">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <span className="text-xs font-bold">HUMAN RECORDING — professionally recorded Punjabi clip</span>
                <span className="flex items-center gap-1.5 shrink-0">
                  {punjabi.upload_available && (
                    <button data-testid="punjabi-sample-upload" onClick={() => playPjSample("upload")}
                      className="flex items-center gap-1 text-[9px] font-bold tracking-widest text-[#D4AF37] border border-[#D4AF37]/50 rounded-full px-2.5 py-1">
                      {pjSample === "playing:upload" ? <Square size={9} /> : <Volume2 size={10} />}
                      {pjSample === "playing:upload" ? "STOP" : "HEAR IT"}
                    </button>
                  )}
                  {punjabi.upload_active ? (
                    <span className="text-[9px] font-black tracking-widest text-black bg-[#D4AF37] rounded-full px-3 py-1">APPROVED</span>
                  ) : punjabi.upload_available ? (
                    <button data-testid="punjabi-approve-upload" onClick={() => approvePunjabi("upload")}
                      className="text-[9px] font-bold tracking-widest text-white/70 border border-white/25 rounded-full px-3 py-1">APPROVE</button>
                  ) : null}
                </span>
              </div>
              <label className="mt-2 inline-flex items-center gap-2 text-[10px] font-bold tracking-widest text-[#D4AF37] border border-dashed border-[#D4AF37]/40 rounded-full px-4 py-2 cursor-pointer">
                {uploading ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />}
                {uploading ? "UPLOADING…" : punjabi.upload_available ? "REPLACE RECORDING (.MP3 / .M4A / .WAV)" : "UPLOAD RECORDING (.MP3 / .M4A / .WAV)"}
                <input data-testid="punjabi-upload-input" type="file" accept=".mp3,.m4a,.wav,audio/*" className="hidden"
                  onChange={(e) => { uploadPunjabi(e.target.files?.[0]); e.target.value = ""; }} />
              </label>
            </div>
            {punjabi.source && (
              <button data-testid="punjabi-off" onClick={() => approvePunjabi("")}
                className="text-[9px] font-bold tracking-widest text-white/50 border border-white/20 rounded-full px-3 py-1.5">
                TURN PUNJABI GREETING OFF
              </button>
            )}
          </div>
        </div>
      )}

      {narrators && (
        <div className="rounded-xl bg-[#121212] border border-white/10 p-4" data-testid="admin-narrators">
          <p className="text-[10px] tracking-widest text-[#D4AF37] mb-1">THE STORYTELLER VOICE — PICK YOUR NARRATOR</p>
          <p className="text-xs text-white/50 mb-3">
            Three professional, fluent American-English storytellers plus the original cloned voice.
            Tap SAMPLE to hear each one tell the Butter Chicken opening, then USE the one you love.
            The chosen voice narrates everything: greetings, stories, mascot lines.
          </p>
          <div className="space-y-2">
            {narrators.narrators.map((n) => (
              <div key={n.id} data-testid={`narrator-${n.id}`}
                className={`rounded-xl border px-4 py-3 ${n.active ? "border-[#D4AF37] bg-[#D4AF37]/10" : "border-white/10 bg-[#1A1A1A]"}`}>
                <div className="flex items-center justify-between gap-2">
                  <span className="text-xs font-bold">{n.name}</span>
                  <span className="flex items-center gap-1.5 shrink-0">
                    <button data-testid={`narrator-sample-${n.id}`} onClick={() => playNarrSample(n.id)}
                      className="flex items-center gap-1 text-[9px] font-bold tracking-widest text-[#D4AF37] border border-[#D4AF37]/50 rounded-full px-2.5 py-1">
                      {narrSample === n.id ? <Loader2 size={10} className="animate-spin" /> : narrSample === `playing:${n.id}` ? <Square size={9} /> : <Volume2 size={10} />}
                      {narrSample === `playing:${n.id}` ? "STOP" : "SAMPLE"}
                    </button>
                    {n.active ? (
                      <span data-testid={`narrator-active-${n.id}`} className="text-[9px] font-black tracking-widest text-black bg-[#D4AF37] rounded-full px-3 py-1">ACTIVE</span>
                    ) : (
                      <button data-testid={`narrator-use-${n.id}`} onClick={() => pickNarrator(n.id)}
                        className="text-[9px] font-bold tracking-widest text-white/70 border border-white/25 rounded-full px-3 py-1">USE</button>
                    )}
                  </span>
                </div>
                <span className="block text-[10px] text-white/50 mt-0.5">{n.desc}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {settings && (
        <div className="rounded-xl bg-[#121212] border border-white/10 p-4" data-testid="admin-narration-controls">
          <p className="text-[10px] tracking-widest text-[#D4AF37] mb-3">NARRATION — MASTER CONTROLS</p>
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="text-xs font-bold">Narration on tablets</p>
              <p className="text-[10px] text-white/50">Off hides every voice button for guests — text and subtitles stay</p>
            </div>
            <button data-testid="narration-toggle" onClick={() => saveNarration({ narration_enabled: !settings.narration_enabled })}
              className={`shrink-0 text-[9px] font-black tracking-widest rounded-full px-4 py-2 border ${settings.narration_enabled ? "bg-[#D4AF37] text-black border-[#D4AF37]" : "border-white/25 text-white/50"}`}>
              {settings.narration_enabled ? "ON" : "OFF"}
            </button>
          </div>
          <div className="flex items-center gap-3">
            <p className="text-xs font-bold shrink-0">Volume</p>
            <input data-testid="narration-volume" type="range" min="0" max="100" value={settings.narration_volume}
              onChange={(e) => setSettings({ ...settings, narration_volume: Number(e.target.value) })}
              onMouseUp={() => saveNarration({ narration_volume: settings.narration_volume })}
              onKeyUp={() => saveNarration({ narration_volume: settings.narration_volume })}
              onBlur={() => saveNarration({ narration_volume: settings.narration_volume })}
              onTouchEnd={() => saveNarration({ narration_volume: settings.narration_volume })}
              className="flex-1 accent-[#D4AF37]" />
            <span className="text-xs font-black text-[#D4AF37] w-10 text-right">{settings.narration_volume}%</span>
          </div>
        </div>
      )}

      {settings && (
        <div className="rounded-xl bg-[#121212] border border-white/10 p-4" data-testid="admin-voice-delivery">
          <p className="text-[10px] tracking-widest text-[#D4AF37] mb-1">HOW VEERGI SPEAKS — PACE & CHARM</p>
          <p className="text-xs text-white/50 mb-3">
            Sounding rushed? Tap SAMPLE to hear each pace first, then USE the one you like.
            Samples record once each; switching re-records every line once at the new pace on its next play.
          </p>
          <div className="space-y-2">
            {DELIVERIES.map((d) => (
              <div
                key={d.id}
                data-testid={`delivery-${d.id}`}
                className={`rounded-xl border px-4 py-3 ${
                  settings.voice_delivery === d.id ? "border-[#D4AF37] bg-[#D4AF37]/10" : "border-white/10 bg-[#1A1A1A]"
                }`}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="text-xs font-bold">{d.name}</span>
                  <span className="flex items-center gap-1.5 shrink-0">
                    <button
                      data-testid={`delivery-sample-${d.id}`}
                      onClick={() => playSample(d.id)}
                      className="flex items-center gap-1 text-[9px] font-bold tracking-widest text-[#D4AF37] border border-[#D4AF37]/50 rounded-full px-2.5 py-1"
                    >
                      {sample === d.id ? <Loader2 size={10} className="animate-spin" /> : sample === `playing:${d.id}` ? <Square size={9} /> : <Volume2 size={10} />}
                      {sample === `playing:${d.id}` ? "STOP" : "SAMPLE"}
                    </button>
                    {settings.voice_delivery === d.id ? (
                      <span data-testid={`delivery-active-${d.id}`} className="text-[9px] font-black tracking-widest text-black bg-[#D4AF37] rounded-full px-3 py-1">ACTIVE</span>
                    ) : (
                      <button data-testid={`delivery-use-${d.id}`} onClick={() => pickDelivery(d.id)}
                        className="text-[9px] font-bold tracking-widest text-white/70 border border-white/25 rounded-full px-3 py-1">USE</button>
                    )}
                  </span>
                </div>
                <span className="block text-[10px] text-white/50 mt-0.5">{d.desc}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {voices && (
        <div className="rounded-xl bg-[#121212] border border-white/10 p-4" data-testid="admin-voice-picker">
          <p className="text-[10px] tracking-widest text-[#D4AF37] mb-1">WHICH VOICE SPEAKS</p>
          <p className="text-xs text-white/50 mb-3">
            Hard to understand? Pick a clearer voice below, then tap HEAR IT on any line to judge it.
            Changing the voice re-records each line once on its next play (small one-time cost per line).
          </p>
          <div className="divide-y divide-white/5">
            {voices.voices.map((v) => (
              <div key={v.voice_id} data-testid={`voice-option-${v.voice_id}`} className="flex items-center gap-3 py-2.5">
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-bold truncate">{v.name}</p>
                  <p className="text-[9px] tracking-widest text-white/40 uppercase">{v.kind}</p>
                </div>
                {v.active ? (
                  <span data-testid={`voice-active-${v.voice_id}`} className="shrink-0 text-[9px] font-black tracking-widest text-black bg-[#D4AF37] rounded-full px-3 py-1.5">ACTIVE</span>
                ) : (
                  <button data-testid={`voice-use-${v.voice_id}`} onClick={() => pickVoice(v.voice_id)}
                    className="shrink-0 text-[9px] font-bold tracking-widest text-[#D4AF37] border border-[#D4AF37]/50 rounded-full px-3 py-1.5">
                    USE THIS VOICE
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="rounded-xl bg-[#121212] border border-white/10 p-4">
        <p className="text-[10px] tracking-widest text-[#D4AF37] mb-3">HOME GREETING — "SAT SRI AKAL, HEAR VEERGI" BUTTON</p>
        <div className="flex gap-2 mb-3">
          {LANGS.map((l) => (
            <button key={l.code} data-testid={`voice-lang-${l.code}`} onClick={() => setLang(l.code)}
              className={`rounded-full px-4 py-2 text-[10px] font-bold tracking-widest border ${lang === l.code ? "bg-[#D4AF37] text-black border-[#D4AF37]" : "bg-[#1A1A1A] text-white/60 border-white/10"}`}>
              {l.label}
            </button>
          ))}
        </div>
        {data.greetings[lang].map((line, i) => (
          <div key={`${lang}-${i}`} className="mb-3">
            <div className="flex items-center justify-between mb-1">
              <p className="text-[9px] tracking-widest text-white/40">
                {data.days[i]}{i === data.today_index && <span className="text-[#D4AF37]"> · PLAYS TODAY</span>}
              </p>
              <PreviewBtn slot={`greeting|${lang}|${i}`} text={line} />
            </div>
            <Area value={line} onChange={(v) => setGreeting(i, v)} testId={`voice-greeting-${lang}-${i}`} highlight={i === data.today_index} />
          </div>
        ))}
      </div>

      <div className="rounded-xl bg-[#121212] border border-white/10 p-4">
        <p className="text-[10px] tracking-widest text-[#D4AF37] mb-3">ATTRACT SCREEN HOOK — "HEAR VEERGI" ON THE PARKED TABLET</p>
        {data.attract.map((line, i) => (
          <div key={`a-${i}`} className="mb-3">
            <div className="flex items-center justify-between mb-1">
              <p className="text-[9px] tracking-widest text-white/40">
                {data.days[i]}{i === data.today_index && <span className="text-[#D4AF37]"> · PLAYS TODAY</span>}
              </p>
              <PreviewBtn slot={`attract|${i}`} text={line} />
            </div>
            <Area value={line} onChange={(v) => setAttract(i, v)} testId={`voice-attract-${i}`} highlight={i === data.today_index} />
          </div>
        ))}
      </div>

      {data.mascot && (
        <div className="rounded-xl bg-[#121212] border border-white/10 p-4" data-testid="admin-mascot-lines">
          <p className="text-[10px] tracking-widest text-[#D4AF37] mb-1">MASCOT REACTIONS — SHORT SPOKEN LINES</p>
          <p className="text-xs text-white/50 mb-3">
            What the little VeerGi mascot says when a guest adds a dish, unlocks a reward or nails a quiz.
            Uses the language tabs above — keep lines short and punchy.
          </p>
          {MASCOT_KINDS.map(({ kind, label }) => (
            <div key={kind} className="mb-3">
              <div className="flex items-center justify-between mb-1">
                <p className="text-[9px] tracking-widest text-white/40">{label}</p>
                <PreviewBtn slot={`mascot|${kind}|${lang}`} text={data.mascot[kind][lang]} />
              </div>
              <Area value={data.mascot[kind][lang]} onChange={(v) => setMascotLine(kind, v)} testId={`voice-mascot-${kind}-${lang}`} />
            </div>
          ))}
        </div>
      )}

      <div className="rounded-xl bg-[#121212] border border-white/10 p-4">
        <div className="flex items-center justify-between mb-2">
          <p className="text-[10px] tracking-widest text-[#D4AF37]">OPENING STING — PLAYS WITH THE LOGO INTRO</p>
          <PreviewBtn slot="intro-sting" text={data.sting} />
        </div>
        <input
          data-testid="voice-sting-input"
          value={data.sting}
          onChange={(e) => setData({ ...data, sting: e.target.value })}
          className="w-full bg-[#1A1A1A] border border-white/10 rounded-xl px-3 py-3 text-xs focus:outline-none focus:border-[#D4AF37]/60"
        />
      </div>

      {pron && (
        <div className="rounded-xl bg-[#121212] border border-white/10 p-4" data-testid="admin-pronunciations">
          <p className="text-[10px] tracking-widest text-[#D4AF37] mb-1">DISH-NAME PRONUNCIATION</p>
          <p className="text-xs text-white/50 mb-3">
            How the narrator says Indian food words — the written menu never changes.
            Edit the spoken spelling, add words, then SAVE. Changed words re-record affected lines once on their next play.
          </p>
          <div className="space-y-2">
            {Object.entries(pron.words).map(([term, spoken]) => (
              <div key={term} className="flex items-center gap-2">
                <span className="w-28 shrink-0 text-xs font-bold capitalize truncate">{term}</span>
                <input data-testid={`pron-${term.replace(/\s+/g, "-")}`} value={spoken}
                  onChange={(e) => setPron({ ...pron, words: { ...pron.words, [term]: e.target.value } })}
                  className="flex-1 min-w-0 bg-[#1A1A1A] border border-white/10 rounded-full px-3 py-2 text-xs focus:outline-none focus:border-[#D4AF37]/60" />
                <button data-testid={`pron-hear-${term.replace(/\s+/g, "-")}`} onClick={() => playPron(term, spoken)} aria-label={`Hear ${term}`}
                  className="shrink-0 flex items-center gap-1 text-[9px] font-bold tracking-widest text-[#D4AF37] border border-[#D4AF37]/50 rounded-full px-2.5 py-1.5">
                  {pronPlaying === term ? <Loader2 size={10} className="animate-spin" /> : pronPlaying === `playing:${term}` ? <Square size={9} /> : <Volume2 size={10} />}
                  {pronPlaying === `playing:${term}` ? "STOP" : "HEAR IT"}
                </button>
                <button data-testid={`pron-del-${term.replace(/\s+/g, "-")}`} aria-label={`Remove ${term}`}
                  onClick={() => { const w = { ...pron.words }; delete w[term]; setPron({ ...pron, words: w }); }}
                  className="shrink-0 text-white/40 hover:text-[#E25822] p-1.5"><Trash2 size={13} /></button>
              </div>
            ))}
            <div className="flex items-center gap-2 pt-1">
              <input data-testid="pron-new-term" value={newTerm} onChange={(e) => setNewTerm(e.target.value)} placeholder="Word (e.g. biryani)"
                className="w-28 shrink-0 bg-[#1A1A1A] border border-white/10 rounded-full px-3 py-2 text-xs focus:outline-none focus:border-[#D4AF37]/60" />
              <input data-testid="pron-new-spoken" value={newSpoken} onChange={(e) => setNewSpoken(e.target.value)} placeholder="Spoken as (e.g. beer-yah-nee)"
                className="flex-1 min-w-0 bg-[#1A1A1A] border border-white/10 rounded-full px-3 py-2 text-xs focus:outline-none focus:border-[#D4AF37]/60" />
              <button data-testid="pron-hear-new" disabled={!newTerm.trim() || !newSpoken.trim()}
                onClick={() => playPron(newTerm.trim().toLowerCase(), newSpoken.trim())} aria-label="Hear new word"
                className="shrink-0 flex items-center gap-1 text-[9px] font-bold tracking-widest text-[#D4AF37] border border-[#D4AF37]/50 rounded-full px-2.5 py-2 disabled:opacity-40">
                <Volume2 size={11} />
              </button>
              <button data-testid="pron-add" disabled={!newTerm.trim() || !newSpoken.trim()}
                onClick={() => {
                  setPron({ ...pron, words: { ...pron.words, [newTerm.trim().toLowerCase()]: newSpoken.trim() } });
                  setNewTerm(""); setNewSpoken("");
                }}
                className="shrink-0 flex items-center gap-1 text-[9px] font-bold tracking-widest text-[#D4AF37] border border-[#D4AF37]/50 rounded-full px-3 py-2 disabled:opacity-40">
                <Plus size={11} /> ADD
              </button>
            </div>
          </div>
          <button data-testid="pron-save" onClick={() => savePron(pron.words)}
            className="w-full mt-4 border border-[#D4AF37]/60 text-[#D4AF37] text-[10px] font-bold rounded-full py-3 tracking-widest">
            SAVE PRONUNCIATIONS
          </button>
        </div>
      )}

      <div className="rounded-xl bg-[#121212] border border-[#E25822]/30 p-4" data-testid="admin-purge-audio">
        <p className="text-[10px] tracking-widest text-[#E25822] mb-1">REGENERATE ALL AUDIO</p>
        <p className="text-xs text-white/50 mb-3">
          Removes every cached clip — including any recorded with an old voice — so all narration re-records fresh
          in the approved voices on its next play. Use after big voice or pronunciation changes.
        </p>
        <button data-testid="purge-audio-btn" onClick={purgeAudio} disabled={purging}
          className="border border-[#E25822]/60 text-[#E25822] text-[10px] font-bold rounded-full px-5 py-2.5 tracking-widest disabled:opacity-50">
          {purging ? "CLEARING…" : "CLEAR & RE-RECORD ALL AUDIO"}
        </button>
      </div>

      <button data-testid="voice-save" onClick={save} disabled={saving}
        className="w-full bg-[#D4AF37] disabled:opacity-60 text-black text-xs font-bold rounded-full py-3.5 tracking-widest">
        {saving ? "SAVING…" : "SAVE VOICE DIALOG"}
      </button>
    </div>
  );
};
